// Discovery Graph is the public introduction network. Adapters supply edge and
// profile reads; this module owns traversal, path selection, limits, hydration,
// and partial-failure reporting so Firestore and demo mode cannot drift apart.

import {
  FACET_CONTEXTS,
  identityOf,
  isDiscoveryEnabled,
  normalizeRolodexEntry,
} from "./rolodexContext";

export const DEFAULT_DISCOVERY_LIMITS = Object.freeze({
  maxDepth: 3,
  // Includes the current user (degree zero).
  maxTotal: 500,
  maxByDegree: Object.freeze({ 1: 200, 2: 150, 3: 150 }),
});

const STRENGTH_RANK = Object.freeze({ strong: 0, moderate: 1, weak: 2 });
const EMPTY_METADATA = Object.freeze({ contexts: [] });

function compareText(left, right) {
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

function comparePaths(left, right) {
  const length = Math.min(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const comparison = compareText(left[index], right[index]);
    if (comparison) return comparison;
  }
  return left.length - right.length;
}

function strengthRank(strength) {
  return STRENGTH_RANK[strength] ?? 3;
}

function finiteLimit(value, fallback, minimum = 0) {
  if (value === Infinity) return Infinity;
  if (!Number.isFinite(value)) return fallback;
  return Math.max(minimum, Math.floor(value));
}

function normalizeLimits(limits = {}) {
  const defaults = DEFAULT_DISCOVERY_LIMITS;
  const maxDepth = finiteLimit(limits.maxDepth, defaults.maxDepth);
  const maxByDegree = {};
  for (let degree = 1; degree <= maxDepth; degree += 1) {
    maxByDegree[degree] = finiteLimit(
      limits.maxByDegree?.[degree],
      defaults.maxByDegree[degree] ?? Infinity
    );
  }
  return {
    maxDepth,
    // The root node always exists, even if an invalid zero limit is supplied.
    maxTotal: finiteLimit(limits.maxTotal, defaults.maxTotal, 1),
    maxByDegree,
  };
}

/** Private owner metadata attached only to the owner's degree-one nodes. */
export function ownDiscoveryMetadata(entries, uid) {
  const entry = (entries || [])
    .map(normalizeRolodexEntry)
    .find((item) => identityOf(item).user_uid === uid);
  if (!entry) return EMPTY_METADATA;

  const contexts = FACET_CONTEXTS.filter((context) => Boolean(entry.facets?.[context]));
  const discoveryContext = contexts.find((context) =>
    isDiscoveryEnabled(entry.facets[context], context)
  ) || contexts[0];
  const discoveryFacet = entry.facets?.[discoveryContext] || {};

  return {
    contexts,
    relationshipType: discoveryFacet.relationship_type,
    connectionStrength: discoveryFacet.connection_strength,
  };
}

function candidateComparison(left, right) {
  if (left.anchorRank !== right.anchorRank) {
    return left.anchorRank - right.anchorRank;
  }
  return comparePaths(left.path, right.path);
}

function outgoingFor(outgoingBySource, sourceUid) {
  if (outgoingBySource instanceof Map) return outgoingBySource.get(sourceUid) || [];
  return outgoingBySource?.[sourceUid] || [];
}

/**
 * Select one deterministic layer without doing I/O.
 *
 * All candidates are shortest paths because callers advance breadth-first.
 * When a target has multiple shortest paths, prefer the path through the
 * strongest degree-one connection, then use the complete UID path as a stable
 * tie-break. Caps apply after that best-path choice, never to adapter order.
 */
export function selectDiscoveryLayer({
  currentUid,
  degree,
  frontier,
  discoveredIds,
  outgoingBySource,
  ownEntries = [],
  limit = Infinity,
}) {
  const bestByTarget = new Map();

  (frontier || []).forEach((parent) => {
    (outgoingFor(outgoingBySource, parent.id) || []).forEach((edge) => {
      const targetUid = edge?.target_uid;
      if (
        typeof targetUid !== "string" ||
        !targetUid ||
        targetUid === currentUid ||
        discoveredIds.has(targetUid) ||
        (edge.source_uid && edge.source_uid !== parent.id)
      ) {
        return;
      }

      const metadata = degree === 1
        ? ownDiscoveryMetadata(ownEntries, targetUid)
        : EMPTY_METADATA;
      const path = [...parent.path, targetUid];
      const candidate = {
        id: targetUid,
        parentUid: parent.id,
        path,
        anchorRank: degree === 1
          ? strengthRank(metadata.connectionStrength)
          : parent.anchorRank,
        metadata,
      };
      const previous = bestByTarget.get(targetUid);
      if (!previous || candidateComparison(candidate, previous) < 0) {
        bestByTarget.set(targetUid, candidate);
      }
    });
  });

  const candidates = [...bestByTarget.values()].sort(candidateComparison);
  const accepted = candidates.slice(0, limit);
  return {
    accepted,
    truncated: accepted.length < candidates.length,
  };
}

function readFailure(stage, uid, error) {
  return {
    stage,
    uid,
    message: error instanceof Error ? error.message : String(error || "Unknown read error"),
  };
}

function countLabel(count, singular, plural = `${singular}s`) {
  return count ? `${count} ${count === 1 ? singular : plural}` : null;
}

/** Convert structured partial failures into the hook's backwards-compatible string. */
export function discoveryErrorMessage(errors) {
  if (!errors?.length) return null;
  const snapshotCount = errors.filter((item) => item.stage === "snapshot").length;
  const branchCount = errors.filter((item) => item.stage === "outgoing").length;
  const profileCount = errors.filter((item) => item.stage === "profile").length;
  const details = [
    countLabel(snapshotCount, "network snapshot"),
    countLabel(branchCount, "connection branch"),
    countLabel(profileCount, "profile"),
  ].filter(Boolean).join(" and ");
  return `The discovery network is incomplete because ${details} could not be loaded.`;
}

function graphNode(candidate, degree) {
  const { contexts } = candidate.metadata;
  return {
    id: candidate.id,
    degree,
    parentUid: candidate.parentUid,
    contexts,
    context: contexts.length === 1 ? contexts[0] : contexts.length > 1 ? "all" : null,
    relationshipType: candidate.metadata.relationshipType,
    connectionStrength: candidate.metadata.connectionStrength,
  };
}

function graphEdge(candidate, degree) {
  return {
    source: candidate.parentUid,
    target: candidate.id,
    degree,
    connectionStrength: candidate.metadata.connectionStrength,
  };
}

/**
 * Hydrate without mutating the traversal or adapter-owned profile objects.
 * `projectProfile` is the policy seam for a privacy-safe public projection.
 */
export async function hydrateDiscoveryGraph(
  graph,
  { loadProfile, projectProfile = (profile) => profile } = {}
) {
  if (typeof loadProfile !== "function") {
    return {
      ...graph,
      nodes: graph.nodes.map((node) => ({ ...node, profile: { id: node.id } })),
    };
  }

  const hydrated = await Promise.all(graph.nodes.map(async (node) => {
    try {
      const rawProfile = await loadProfile(node.id);
      if (!rawProfile) return { node: { ...node, profile: { id: node.id } } };
      const projected = projectProfile({ ...rawProfile, id: node.id }, node);
      const profile = projected && typeof projected === "object"
        ? { ...projected, id: node.id }
        : { id: node.id };
      return { node: { ...node, profile } };
    } catch (error) {
      return {
        node: { ...node, profile: { id: node.id } },
        error: readFailure("profile", node.id, error),
      };
    }
  }));

  return {
    ...graph,
    nodes: hydrated.map((item) => item.node),
    errors: [...(graph.errors || []), ...hydrated.flatMap((item) => item.error ? [item.error] : [])],
  };
}

/**
 * Build an introduction graph through an adapter with `loadOutgoing(uid)` and
 * optional `loadProfile(uid)` methods. The same walk powers every adapter.
 */
export async function buildDiscoveryGraph({
  currentUid,
  ownEntries = [],
  adapter,
  limits,
  projectProfile,
}) {
  if (!currentUid) {
    return {
      nodes: [], edges: [], truncated: false, partial: false, errors: [], error: null,
    };
  }
  if (!adapter || typeof adapter.loadOutgoing !== "function") {
    throw new TypeError("A discovery adapter with loadOutgoing(uid) is required.");
  }

  const policy = normalizeLimits(limits);
  const nodes = [{ id: currentUid, degree: 0, parentUid: null }];
  const edges = [];
  const errors = [];
  const discoveredIds = new Set([currentUid]);
  let frontier = [{ id: currentUid, path: [currentUid], anchorRank: 3 }];
  let truncated = false;

  for (let degree = 1; degree <= policy.maxDepth && frontier.length; degree += 1) {
    const orderedFrontier = [...frontier].sort(candidateComparison);
    const reads = await Promise.all(orderedFrontier.map(async (parent) => {
      try {
        const outgoing = await adapter.loadOutgoing(parent.id);
        return { parent, outgoing: Array.isArray(outgoing) ? outgoing : [] };
      } catch (error) {
        return { parent, outgoing: [], error: readFailure("outgoing", parent.id, error) };
      }
    }));

    const outgoingBySource = new Map();
    reads.forEach(({ parent, outgoing, error }) => {
      outgoingBySource.set(parent.id, outgoing);
      if (error) errors.push(error);
    });

    const remainingTotal = Math.max(0, policy.maxTotal - nodes.length);
    const layerLimit = Math.min(policy.maxByDegree[degree], remainingTotal);
    const layer = selectDiscoveryLayer({
      currentUid,
      degree,
      frontier: orderedFrontier,
      discoveredIds,
      outgoingBySource,
      ownEntries,
      limit: layerLimit,
    });
    truncated = truncated || layer.truncated;

    layer.accepted.forEach((candidate) => {
      discoveredIds.add(candidate.id);
      nodes.push(graphNode(candidate, degree));
      edges.push(graphEdge(candidate, degree));
    });
    frontier = layer.accepted;
  }

  const hydrated = await hydrateDiscoveryGraph(
    { nodes, edges, truncated, errors },
    { loadProfile: adapter.loadProfile, projectProfile }
  );
  const allErrors = hydrated.errors || [];
  return {
    ...hydrated,
    partial: allErrors.length > 0,
    error: discoveryErrorMessage(allErrors),
  };
}

/** In-memory adapter used by demo mode and deterministic tests. */
export function createInMemoryDiscoveryAdapter({ edges = [], profiles = {} } = {}) {
  const outgoingBySource = new Map();
  (edges || []).forEach((edge) => {
    if (!edge?.source_uid) return;
    if (!outgoingBySource.has(edge.source_uid)) outgoingBySource.set(edge.source_uid, []);
    outgoingBySource.get(edge.source_uid).push({ ...edge });
  });
  const profileByUid = profiles instanceof Map ? new Map(profiles) : new Map(Object.entries(profiles));

  return {
    kind: "memory",
    async loadOutgoing(uid) {
      return (outgoingBySource.get(uid) || []).map((edge) => ({ ...edge }));
    },
    async loadProfile(uid) {
      const profile = profileByUid.get(uid);
      return profile ? { ...profile } : null;
    },
  };
}

/**
 * Adapt a complete topology snapshot while leaving profile hydration on its
 * own adapter. Firestore's live collection listener and static demo data both
 * enter the traversal through this shape.
 */
export function createSnapshotDiscoveryAdapter({ edges = [], profiles = {}, loadProfile } = {}) {
  const topology = createInMemoryDiscoveryAdapter({ edges, profiles });
  return {
    kind: "snapshot",
    loadOutgoing: topology.loadOutgoing,
    loadProfile: typeof loadProfile === "function" ? loadProfile : topology.loadProfile,
  };
}
